/**
 * NetworkSync Service
 * 
 * Client-side WebSocket management for RollWords multiplayer.
 * Handles connection lifecycle, event subscriptions, and communication with backend.
 */

import { io, Socket } from 'socket.io-client';
import type { EndTurnPayload } from '../types/NetworkTypes';

// ==================== Configuration & Validation ====================

/**
 * Resolves multiplayer backend endpoint from environment variables
 * with production-specific validation.
 * 
 * Environment Variables:
 * - VITE_MULTIPLAYER_SERVER_URL: Backend endpoint (required)
 * - VITE_MULTIPLAYER_RECONNECT_WINDOW_MINUTES: Reconnect policy (default: 5)
 * 
 * Production Rules:
 * - Must use HTTPS/WSS protocol
 * - Cannot be localhost
 * - Must be absolute URL
 */
export function resolveMultiplayerConfig(): {
  serverUrl: string;
  reconnectWindowMinutes: number;
  isProduction: boolean;
} {
  const serverUrl = import.meta.env.VITE_MULTIPLAYER_SERVER_URL || 'http://localhost:3001';
  const reconnectMinutes = parseInt(
    import.meta.env.VITE_MULTIPLAYER_RECONNECT_WINDOW_MINUTES || '5',
    10
  );
  
  const isProduction = serverUrl.startsWith('https://');
  
  // Validate production endpoint requirements
  if (isProduction) {
    validateProductionEndpoint(serverUrl);
  }
  
  return {
    serverUrl,
    reconnectWindowMinutes: reconnectMinutes,
    isProduction
  };
}

/**
 * Validates that production endpoint meets security requirements.
 * Throws error if validation fails.
 */
function validateProductionEndpoint(url: string): void {
  try {
    const endpoint = new URL(url);
    
    // Enforce HTTPS in production
    if (endpoint.protocol !== 'https:') {
      throw new Error(
        `Production multiplayer endpoint must use HTTPS, got: ${endpoint.protocol}`
      );
    }
    
    // Reject localhost in production
    if (
      endpoint.hostname === 'localhost' ||
      endpoint.hostname === '127.0.0.1' ||
      endpoint.hostname === '::1'
    ) {
      throw new Error(
        'Production multiplayer endpoint cannot be localhost. ' +
        'Update VITE_MULTIPLAYER_SERVER_URL to your production api.<domain>'
      );
    }
    
    // Validate absolute URL
    if (!endpoint.href) {
      throw new Error('Multiplayer endpoint must be an absolute URL');
    }
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(`Invalid multiplayer endpoint URL: ${url}`);
    }
    throw error;
  }
}

/**
 * Determines WebSocket transport policy based on environment.
 * Production must use WSS (WebSocket Secure only).
 * Development may fall back to WS for localhost testing.
 */
export function resolveTransportPolicy(isProduction: boolean): {
  transports: ('websocket' | 'polling')[];
  upgrade: boolean;
  secure: boolean;
} {
  if (isProduction) {
    // Production: WSS only, no fallback to insecure protocols
    return {
      transports: ['websocket'],
      upgrade: false,
      secure: true
    };
  }
  
  // Development: Allow both WS and polling for flexibility
  return {
    transports: ['websocket', 'polling'],
    upgrade: true,
    secure: false
  };
}

// Inline all necessary types to avoid module resolution issues
const ConnectionStatus = {
  CONNECTED: 'CONNECTED',
  DISCONNECTED: 'DISCONNECTED',
  RECONNECTING: 'RECONNECTING',
} as const;
type ConnectionStatus = typeof ConnectionStatus[keyof typeof ConnectionStatus];

interface CreateSessionPayload {
  displayName: string;
  maxPlayers?: number;
  settings?: any;
}

interface JoinSessionPayload {
  shareCode: string;
  displayName: string;
}

interface GameActionPayload {
  actionType: string;
  data: any;
  timestamp: Date;
  playerId: string;
}

// Define these interfaces temporarily until module resolution is fixed
interface ClientToServerEvents {
  create_session: (payload: CreateSessionPayload) => void;
  join_session: (payload: JoinSessionPayload) => void;
  leave_session: () => void;
  restore_session: (payload: { sessionId: string; displayName: string }) => void;
  game_action: (payload: GameActionPayload) => void;
  end_turn: (payload?: { currentRackLetters?: any[]; cueBallPosition?: { x: number; y: number } | null }) => void;
  skip_turn: () => void;
  start_game: () => void;
  board_sync_request: (payload: {
    sessionId: string;
    lastKnownBoardRevision: number;
    reason: 'MISSING_SNAPSHOT' | 'STALE_SNAPSHOT';
  }) => void;
}

interface ServerToClientEvents {
  connection_ack: (payload: { connectionId: string; serverTime: Date }) => void;
  disconnect: (payload: any) => void;
  session_created: (payload: any) => void;
  session_joined: (payload: any) => void;
  join_error: (payload: any) => void;
  player_joined: (payload: any) => void;
  player_left: (payload: any) => void;
  game_started: (payload: any) => void;
  session_restored: (payload: any) => void;
  player_reconnected: (payload: any) => void;
  restore_failed: (payload: any) => void;
  game_action_broadcast: (payload: any) => void;
  game_action: (payload: any) => void;
  game_state_update: (payload: any) => void;
  action_accepted: (payload: { actionId: string }) => void;
  action_rejected: (payload: { actionId: string; reason: string }) => void;
  turn_change: (payload: any) => void;
  turn_timer: (payload: any) => void;
}

export interface NetworkSyncConfig {
  serverUrl?: string;
  autoConnect?: boolean;
  reconnection?: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
}

export interface ConnectionState {
  status: ConnectionStatus;
  connectionId: string | null;
  serverTime: Date | null;
  latency: number;
  lastError: string | null;
}

export type EventSubscription = {
  unsubscribe: () => void;
};

export class NetworkSync {
  private static instance: NetworkSync | null = null;
  private socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
  private config: NetworkSyncConfig;
  private connectionState: ConnectionState;
  private eventCallbacks: Map<keyof ServerToClientEvents, Function[]> = new Map();
  private reconnectTimer: NodeJS.Timeout | null = null;
  private latencyTimer: NodeJS.Timeout | null = null;
  private lastSessionId: string | null = null;
  
  private constructor(config: NetworkSyncConfig = {}) {
    // Resolve multiplayer configuration from environment
    const multiplayerConfig = resolveMultiplayerConfig();
    
    this.config = {
      serverUrl: config.serverUrl || multiplayerConfig.serverUrl,
      autoConnect: config.autoConnect ?? true,
      reconnection: config.reconnection ?? true,
      reconnectionAttempts: config.reconnectionAttempts ?? 5,
      reconnectionDelay: config.reconnectionDelay ?? 1000,
      ...config
    };

    this.connectionState = {
      status: ConnectionStatus.DISCONNECTED,
      connectionId: null,
      serverTime: null,
      latency: 0,
      lastError: null
    };

    if (this.config.autoConnect) {
      this.connect();
    }
  }

  // ==================== Singleton Pattern ====================

  public static getInstance(config?: NetworkSyncConfig): NetworkSync {
    if (!NetworkSync.instance) {
      NetworkSync.instance = new NetworkSync(config);
    }
    return NetworkSync.instance;
  }

  public static resetInstance(): void {
    if (NetworkSync.instance) {
      NetworkSync.instance.disconnect();
      NetworkSync.instance = null;
    }
  }

  // ==================== Connection Management ====================

  public connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        resolve();
        return;
      }

      console.log('[NetworkSync] Connecting to server...', this.config.serverUrl);
      
      // Determine transport policy based on server URL
      const multiplayerConfig = resolveMultiplayerConfig();
      const transportPolicy = resolveTransportPolicy(multiplayerConfig.isProduction);

      this.socket = io(this.config.serverUrl!, {
        transports: transportPolicy.transports,
        upgrade: transportPolicy.upgrade,
        timeout: 20000,
        forceNew: true,
        // Enforce secure WebSocket in production
        secure: transportPolicy.secure,
        rejectUnauthorized: multiplayerConfig.isProduction
      });

      this.setupEventHandlers();

      // Connection success
      this.socket.once('connect', () => {
        this.connectionState.status = ConnectionStatus.CONNECTED;
        console.log('[NetworkSync] Connected to server with transport:', this.socket?.io.engine.transport.name);
        resolve();
      });

      // Connection failure
      this.socket.once('connect_error', (error) => {
        this.connectionState.status = ConnectionStatus.DISCONNECTED;
        this.connectionState.lastError = error.message;
        console.error('[NetworkSync] Connection failed:', error);
        reject(error);
      });

      // Timeout
      setTimeout(() => {
        if (this.connectionState.status !== ConnectionStatus.CONNECTED) {
          reject(new Error('Connection timeout'));
        }
      }, 10000);
    });
  }

  public disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.latencyTimer) {
      clearInterval(this.latencyTimer);
      this.latencyTimer = null;
    }

    if (this.socket) {
      console.log('[NetworkSync] Disconnecting from server');
      this.socket.disconnect();
      this.socket = null;
    }

    this.connectionState.status = ConnectionStatus.DISCONNECTED;
    this.connectionState.connectionId = null;
    this.connectionState.serverTime = null;
  }

  // ==================== Event Handler Setup ====================

  private setupEventHandlers(): void {
    if (!this.socket) return;

    // Connection acknowledgment
    this.socket.on('connection_ack', (payload) => {
      this.connectionState.connectionId = payload.connectionId;
      this.connectionState.serverTime = new Date(payload.serverTime);
      this.calculateLatency();
      console.log('[NetworkSync] Connection acknowledged:', payload.connectionId);
      this.triggerCallbacks('connection_ack', payload);
    });

    // Disconnection handling
    this.socket.on('disconnect', (reason) => {
      this.connectionState.status = ConnectionStatus.DISCONNECTED;
      console.log('[NetworkSync] Disconnected:', reason);
      
      if (this.config.reconnection && reason !== 'io client disconnect') {
        this.attemptReconnection();
      }
      
      this.triggerCallbacks('disconnect', { reason: 'client_disconnect', message: reason });
    });

    // Session management events
    this.socket.on('session_created', (payload) => {
      console.log('[NetworkSync] Session created:', payload.shareCode);
      this.lastSessionId = payload.sessionId;
      this.triggerCallbacks('session_created', payload);
    });

    this.socket.on('session_joined', (payload) => {
      console.log('[NetworkSync] Session joined:', payload.sessionId);
      this.lastSessionId = payload.sessionId;
      this.triggerCallbacks('session_joined', payload);
    });

    this.socket.on('join_error', (payload) => {
      console.warn('[NetworkSync] Join error:', payload.message);
      this.triggerCallbacks('join_error', payload);
    });

    this.socket.on('player_joined', (payload) => {
      console.log('[NetworkSync] Player joined:', payload.player.displayName);
      this.triggerCallbacks('player_joined', payload);
    });

    this.socket.on('player_left', (payload) => {
      console.log('[NetworkSync] Player left:', payload.displayName);
      this.triggerCallbacks('player_left', payload);
    });

    this.socket.on('game_started', (payload) => {
      console.log('[NetworkSync] Game started');
      this.triggerCallbacks('game_started', payload);
    });

    this.socket.on('player_reconnected', (payload) => {
      console.log('[NetworkSync] Player reconnected:', payload.displayName);
      this.triggerCallbacks('player_reconnected', payload);
    });

    this.socket.on('session_restored', (payload) => {
      console.log('[NetworkSync] Session restored:', payload.sessionId);
      this.lastSessionId = payload.sessionId;
      this.triggerCallbacks('session_restored', payload);
    });

    this.socket.on('restore_failed', (payload) => {
      console.warn('[NetworkSync] Session restore failed:', payload.message);
      this.triggerCallbacks('restore_failed', payload);
    });

    // Game state events
    this.socket.on('game_state_update', (payload) => {
      // T018: Forward full authoritative payload as-is (no client-side delta reconstruction).
      console.log('[NetworkSync] Game state update received');
      this.triggerCallbacks('game_state_update', payload);
    });

    this.socket.on('game_action_broadcast', (payload) => {
      console.log('[NetworkSync] Game action broadcast received:', payload.actionType);
      this.triggerCallbacks('game_action', payload);
    });

    this.socket.on('action_accepted', (payload) => {
      console.log('[NetworkSync] Action accepted:', payload.actionId);
      this.triggerCallbacks('action_accepted', payload);
    });

    this.socket.on('action_rejected', (payload) => {
      console.warn('[NetworkSync] Action rejected:', payload.reason);
      this.triggerCallbacks('action_rejected', payload);
    });

    // Turn management events
    this.socket.on('turn_change', (payload) => {
      // T018: Forward full turn handoff snapshot payload as-is.
      console.log('[NetworkSync] Turn changed to player:', payload.nextPlayerNumber);
      this.triggerCallbacks('turn_change', payload);
    });

    this.socket.on('turn_timer', (payload) => {
      this.triggerCallbacks('turn_timer', payload);
    });

    // Start latency monitoring
    this.startLatencyMonitoring();
  }

  // ==================== Event Subscription System ====================

  public on<T extends keyof ServerToClientEvents>(
    event: T,
    callback: ServerToClientEvents[T]
  ): EventSubscription {
    if (!this.eventCallbacks.has(event)) {
      this.eventCallbacks.set(event, []);
    }
    
    this.eventCallbacks.get(event)!.push(callback);

    return {
      unsubscribe: () => {
        const callbacks = this.eventCallbacks.get(event);
        if (callbacks) {
          const index = callbacks.indexOf(callback);
          if (index > -1) {
            callbacks.splice(index, 1);
          }
        }
      }
    };
  }

  public off<T extends keyof ServerToClientEvents>(event: T, callback?: Function): void {
    if (!callback) {
      // Remove all callbacks for this event
      this.eventCallbacks.delete(event);
      return;
    }

    const callbacks = this.eventCallbacks.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  private triggerCallbacks<T extends keyof ServerToClientEvents>(
    event: T,
    payload: Parameters<ServerToClientEvents[T]>[0]
  ): void {
    const callbacks = this.eventCallbacks.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(payload);
        } catch (error) {
          console.error(`[NetworkSync] Error in ${event} callback:`, error);
        }
      });
    }
  }

  // ==================== Session Management API ====================

  public async createSession(payload: CreateSessionPayload): Promise<void> {
    if (!this.isConnected()) {
      throw new Error('Not connected to server');
    }
    
    console.log('[NetworkSync] Creating session for:', payload.displayName);
    this.socket!.emit('create_session', payload);
  }

  public async joinSession(payload: JoinSessionPayload): Promise<void> {
    if (!this.isConnected()) {
      throw new Error('Not connected to server');
    }
    
    console.log('[NetworkSync] Joining session:', payload.shareCode);
    this.socket!.emit('join_session', payload);
  }

  public leaveSession(): void {
    if (!this.isConnected()) {
      console.warn('[NetworkSync] Cannot leave session - not connected');
      return;
    }
    
    console.log('[NetworkSync] Leaving session');
    this.socket!.emit('leave_session');
  }

  // ==================== Game Actions API ====================

  public startGame(): void {
    if (!this.isConnected()) {
      console.warn('[NetworkSync] Cannot start game - not connected');
      return;
    }
    
    console.log('[NetworkSync] Starting game');
    this.socket!.emit('start_game');
  }

  public sendGameAction(payload: any): void {
    if (!this.isConnected()) {
      console.warn('[NetworkSync] Cannot send game action - not connected');
      return;
    }
    
    console.log('[NetworkSync] Sending game action:', payload.actionType);
    this.socket!.emit('game_action', payload);
  }

  public endTurn(payload?: EndTurnPayload): void {
    if (!this.isConnected()) {
      console.warn('[NetworkSync] Cannot end turn - not connected');
      return;
    }
    
    console.log('[NetworkSync] Ending turn');
    this.socket!.emit('end_turn', payload);
  }

  public skipTurn(): void {
    if (!this.isConnected()) {
      console.warn('[NetworkSync] Cannot skip turn - not connected');
      return;
    }

    console.log('[NetworkSync] Skipping turn');
    this.socket!.emit('skip_turn');
  }

  public restoreSession(payload: { sessionId: string; displayName: string }): void {
    if (!this.isConnected()) {
      console.warn('[NetworkSync] Cannot restore session - not connected');
      return;
    }

    console.log('[NetworkSync] Sending restore_session for', payload.displayName);
    this.socket!.emit('restore_session', payload);
  }

  public requestBoardResync(payload: {
    sessionId: string;
    lastKnownBoardRevision: number;
    reason: 'MISSING_SNAPSHOT' | 'STALE_SNAPSHOT';
  }): void {
    if (!this.isConnected()) {
      console.warn('[NetworkSync] Cannot request board resync - not connected');
      return;
    }

    console.log('[NetworkSync] Requesting board resync:', payload.reason, payload.lastKnownBoardRevision);
    // Emit as explicit sync request so server can replay authoritative snapshot immediately.
    (this.socket as any).emit('board_sync_request', payload);
  }

  // ==================== Connection State & Health ====================

  public isConnected(): boolean {
    return this.socket?.connected === true;
  }

  public getConnectionState(): ConnectionState {
    return { ...this.connectionState };
  }

  public getLatency(): number {
    return this.connectionState.latency;
  }

  // ==================== Reconnection Logic ====================

  private attemptReconnection(): void {
    this.connectionState.status = ConnectionStatus.RECONNECTING;
    
    let attempts = 0;
    const maxAttempts = this.config.reconnectionAttempts!;
    
    const reconnect = () => {
      if (attempts >= maxAttempts) {
        console.error('[NetworkSync] Reconnection failed after', maxAttempts, 'attempts');
        this.connectionState.status = ConnectionStatus.DISCONNECTED;
        return;
      }

      attempts++;
      console.log(`[NetworkSync] Reconnection attempt ${attempts}/${maxAttempts}`);

      this.connect()
        .then(() => {
          console.log('[NetworkSync] Reconnection successful');
        })
        .catch(() => {
          this.reconnectTimer = setTimeout(reconnect, this.config.reconnectionDelay! * attempts);
        });
    };

    this.reconnectTimer = setTimeout(reconnect, this.config.reconnectionDelay!);
  }

  // ==================== Latency Monitoring ====================

  private calculateLatency(): void {
    if (!this.connectionState.serverTime) return;
    
    const now = Date.now();
    const serverTime = new Date(this.connectionState.serverTime).getTime();
    this.connectionState.latency = Math.abs(now - serverTime);
  }

  private startLatencyMonitoring(): void {
    this.latencyTimer = setInterval(() => {
      if (this.isConnected() && this.connectionState.serverTime) {
        this.calculateLatency();
      }
    }, 5000); // Update every 5 seconds
  }

  // ==================== Debug & Monitoring ====================

  public getStats() {
    return {
      connected: this.isConnected(),
      connectionState: this.getConnectionState(),
      lastSessionId: this.lastSessionId,
      subscribedEvents: Array.from(this.eventCallbacks.keys()),
      totalSubscriptions: Array.from(this.eventCallbacks.values())
        .reduce((sum, callbacks) => sum + callbacks.length, 0),
      config: this.config
    };
  }

  public getConnectionId(): string | undefined {
    return this.socket?.id;
  }

  public getDebugInfo() {
    return {
      ...this.getStats(),
      socketId: this.socket?.id,
      socketConnected: this.socket?.connected,
      socketDisconnected: this.socket?.disconnected,
      eventCallbacks: Object.fromEntries(
        Array.from(this.eventCallbacks.entries()).map(([event, callbacks]) => 
          [event, callbacks.length]
        )
      )
    };
  }
}